export const onDragEndMultiple = (
  { source, destination, draggableId },
  {
    elements = [],
    onChange = () => {},
    onSelect = () => {},
    addSnackbarError = () => {},
    allowDragOut = false,
  },
) => {
  if (!destination) return;

  const { droppableId: fromId, index: fromIndex } = source;
  const { droppableId: toId, index: toIndex } = destination;

  // No-op for same-column reorder
  if (fromId === toId) return;

  try {
    const newState = structuredClone(elements);

    // Use string comparison so numeric and string IDs both work
    const findParent = (id) =>
      newState.find((p) => String(p.id) === String(id));

    const fromParent = findParent(fromId);
    if (!fromParent) throw new Error("Source column not found");

    const draggedIndex = fromParent.elements.findIndex(
      (b) => String(b.id) === String(draggableId),
    );
    if (draggedIndex === -1) throw new Error("Dragged item not found");

    const [draggedElement] = fromParent.elements.splice(draggedIndex, 1);

    const toParent = findParent(toId);
    const dragInfo = { draggableId, fromId, toId, toIndex };
    if (toParent) {
      toParent.elements.splice(toIndex, 0, draggedElement);
      onChange(newState, dragInfo);
    } else if (allowDragOut) {
      newState.push(draggedElement);
      onChange(newState, dragInfo);
      onSelect(draggedElement.id);
    } else {
      throw new Error("Drag out to unknown column is not allowed");
    }
  } catch (e) {
    addSnackbarError({ message: e.message });
  }
};

export const onDragEnd = ({ elements = [], onChange = () => {}, result }) => {
  if (!result?.destination) return;
  const { source, destination } = result;

  if (source?.droppableId === destination?.droppableId) {
    const copiedItems = [...elements];
    const [removed] = copiedItems?.splice(source.index, 1);
    copiedItems.splice(destination.index, 0, removed);

    const updatedItems = copiedItems.map((item, index) => {
      return {
        ...item,
        order: index,
      };
    });

    onChange(updatedItems);
  }
};

const queryAttr = "data-rbd-droppable-id";
const draggableQueryAttr = "data-rbd-draggable-id";

export const onDragUpdate = ({ update, onChange, invert }) => {
  if (!update.destination) return;

  const draggableId = update.draggableId;
  const destinationIndex = update.destination.index;
  const destinationId = update.destination.droppableId;

  const domQueryDrop = `[${queryAttr}='${destinationId}']`;
  const droppableContainer = document.querySelector(domQueryDrop);
  const childDropped = `.HubDroppable-box-${destinationIndex}`;
  const droppedDOM = droppableContainer?.querySelector(childDropped);

  const domQueryDrag = `[${draggableQueryAttr}='${draggableId}']`;
  const draggedDOM = document.querySelector(domQueryDrag);

  if (!draggedDOM) return;
  const { clientHeight, clientWidth } = draggedDOM;

  let clientY = 0;
  if (droppedDOM) {
    const parentRect = droppedDOM.parentNode.getBoundingClientRect();
    const itemRect = droppedDOM.getBoundingClientRect();
    clientY = itemRect.top - parentRect.top - clientHeight;
  }

  const clientYInvert =
    parseFloat(window.getComputedStyle(draggedDOM.parentNode).paddingTop) +
    [...draggedDOM.parentNode.children]
      .slice(0, destinationIndex)
      .reduce((total, curr) => {
        const style = curr.currentStyle || window.getComputedStyle(curr);
        const marginBottom = parseFloat(style.marginBottom);
        return total + curr.clientHeight + marginBottom;
      }, 0);

  onChange({
    clientHeight,
    clientWidth,
    clientY,
    clientYInvert,
    clientX: parseFloat(
      window.getComputedStyle(draggedDOM.parentNode).paddingLeft,
    ),
    destinationId,
    destinationIndex,
  });
};
